import { getStatusLabel } from '../../../status-labels';
import type { SalesOrderStatus } from '../../../types';
import { definePolicy, type Policy } from '../kernel';
import { type Action, allow, type Decision, type Denied, deny } from '../types';

export type SalesOrderFacts = {
  status: SalesOrderStatus;
  isEdiOrder: boolean;
  isCustomerCreated: boolean;
  isExternalOrder: boolean;
  isTemplate: boolean;
};

export const SALES_ORDER_FIELDS = [
  'label',
  'customer',
  'customerContact',
  'customerPO',
  'orderDate',
  'shipTo',
  'billTo',
  'salesTerms',
  'salesPerson',
  'shipFrom',
  'currencyCode',
  'exchangeRate',
  'subsidiary',
  'pricingSheet',
  'salesOrderCustomField',
  'shippingTerms',
  'freightCompany',
  'shipDate',
  'deliveryDate',
  'temperatureMin',
  'temperatureMax',
  'shipmentCustomField',
  'flagged',
] as const;

export type SalesOrderField = (typeof SALES_ORDER_FIELDS)[number];

export const SALES_ORDER_OPS = [
  'submit',
  'approve',
  'start picking',
  'mark as shipped',
  'close',
  'revert',
  'cancel',
  'uncancel',
  'duplicate',
  'add freight',
  'generate document',
  'save as template',
  'set default template',
  'set status directly',
  'handle change requests',
] as const;

export type SalesOrderOp = (typeof SALES_ORDER_OPS)[number];

/**
 * The lifecycle events the Sales Order state machine accepts
 * (api/src/services/salesOrders/salesOrder.states.ts). Kept here so the api door and the web
 * button ask the policy the SAME question about the SAME event.
 */
export const SALES_ORDER_LIFECYCLE_EVENTS = [
  'submit',
  'pick',
  'mark_as_shipped',
  'close',
  'revert',
  'cancel',
  'uncancel',
] as const;

export type SalesOrderLifecycleEvent = (typeof SALES_ORDER_LIFECYCLE_EVENTS)[number];

/**
 * ONE event -> operation mapping. `submit` is the operation "approve" while an EDI order sits at
 * PENDING — the machine normalises PENDING to its `draft` state, so the same event carries two
 * user-facing names and the policy is asked for the one the status implies.
 */
export const salesOrderLifecycleOperation = (
  status: SalesOrderStatus,
  event: SalesOrderLifecycleEvent
): SalesOrderOp => {
  switch (event) {
    case 'submit':
      return status === 'PENDING' ? 'approve' : 'submit';
    case 'pick':
      return 'start picking';
    case 'mark_as_shipped':
      return 'mark as shipped';
    case 'close':
      return 'close';
    case 'revert':
      return 'revert';
    case 'cancel':
      return 'cancel';
    case 'uncancel':
      return 'uncancel';
  }
};

/** The header primary button and confirmation action labels. */
const EVENT_LABELS: Readonly<Record<string, string>> = {
  submit: 'Submit',
  pick: 'Start Picking',
  mark_as_shipped: 'Confirm Shipped',
  close: 'Close',
  revert: 'Revert Sales Order',
  cancel: 'Cancel Sales Order',
  uncancel: 'Uncancel Sales Order',
};

export const salesOrderEventUnavailableMessage = (
  facts: Pick<SalesOrderFacts, 'status' | 'isTemplate'>,
  event: string
): string => {
  const action = event === 'submit' && facts.status === 'PENDING' ? 'Approve' : (EVENT_LABELS[event] ?? 'This action');
  return facts.isTemplate
    ? `${action} isn't available for a sales order template.`
    : `${action} isn't available while the sales order is ${getStatusLabel(facts.status)}.`;
};

const TO_SUBMITTED: readonly SalesOrderStatus[] = ['DRAFT', 'SUBMITTED'];
const TO_PICKING: readonly SalesOrderStatus[] = ['DRAFT', 'PENDING', 'SUBMITTED', 'APPROVED', 'PICKING'];
const TO_SHIPPED: readonly SalesOrderStatus[] = [...TO_PICKING, 'SHIPPED'];
const TO_CLOSED: readonly SalesOrderStatus[] = [...TO_SHIPPED, 'CLOSED'];

const statusAllowsField = (facts: SalesOrderFacts, field: SalesOrderField): boolean => {
  switch (field) {
    case 'label':
      return facts.status === 'DRAFT';
    case 'customer':
      return TO_SUBMITTED.includes(facts.status);
    case 'customerPO':
    case 'shipTo':
    case 'shipFrom':
      return TO_PICKING.includes(facts.status);
    case 'orderDate':
    case 'billTo':
    case 'shippingTerms':
    case 'freightCompany':
    case 'shipDate':
    case 'deliveryDate':
    case 'temperatureMin':
    case 'temperatureMax':
    case 'currencyCode':
    case 'exchangeRate':
      return TO_SHIPPED.includes(facts.status);
    case 'salesTerms':
    case 'salesPerson':
    case 'subsidiary':
      return TO_CLOSED.includes(facts.status);
    case 'customerContact':
    case 'pricingSheet':
    case 'salesOrderCustomField':
    case 'shipmentCustomField':
    case 'flagged':
      return true;
  }
};

/**
 * THE label lock, as one denial. The kernel returns it for `edit label` outside Draft, and the api
 * throws the SAME denial when the label write's `status: 'DRAFT'` predicate misses (P2025) — the
 * order left Draft between the read and the write — so both paths read one sentence.
 */
export const salesOrderLabelLocked = (): Denied =>
  deny('SALES_ORDER_LABEL_LOCKED', 'The label can only be changed in Draft.');

const decideField = (facts: SalesOrderFacts, field: SalesOrderField): Decision => {
  if (field === 'customerContact') return allow();

  if (facts.isEdiOrder && (field === 'customer' || field === 'customerPO')) {
    return deny('SALES_ORDER_EDI_FIELD_LOCKED', 'Set by the EDI order.');
  }

  if (facts.isCustomerCreated && (field === 'customer' || field === 'shipTo' || field === 'billTo')) {
    return deny('SALES_ORDER_CUSTOMER_CREATED_FIELD_LOCKED', "Set by the customer's order.");
  }

  if (facts.isExternalOrder && field === 'shipFrom') {
    return deny('SALES_ORDER_EXTERNAL_FIELD_LOCKED', 'Ship From comes from the linked purchase orders.');
  }

  if (statusAllowsField(facts, field)) return allow();

  return field === 'label'
    ? salesOrderLabelLocked()
    : deny('SALES_ORDER_FIELD_LOCKED', `This field is locked once the sales order is ${getStatusLabel(facts.status)}.`);
};

/**
 * THE STATE MACHINE IS THE AUTHORITY ON LIFECYCLE AVAILABILITY.
 *
 *   api/src/services/salesOrders/salesOrder.states.ts
 *     getOrderStatus:  PENDING ──▶ draft      APPROVED ──▶ submitted
 *     draft      { submit, cancel }
 *     submitted  { pick, cancel, revert, mark_as_shipped }   ← no sourcing guard on pick;
 *                                                              mark_as_shipped's two cond
 *                                                              branches (some !external /
 *                                                              every external) together cover
 *                                                              EVERY line-item set
 *
 * These two pairs are why the windows below are status sets, not hand-written cases: a policy
 * narrower than the table denies a transition that ships orders today. Pinned cell-by-cell in
 * api/src/services/salesOrders/salesOrder.lifecycle.parity.test.ts.
 *
 * `isTemplate` IS a lifecycle condition, in both halves. A template's `status` column exists but
 * carries no meaning — a template is a shape to copy, not an order to run — so no lifecycle
 * operation applies to one. The table refuses the matching event via its `isNotTemplate` cond;
 * this denies the operation the same way, and the parity test pins the two together. Using a
 * template creates a NEW DRAFT order, and that order transitions normally.
 */
const LIFECYCLE_DRAFT: readonly SalesOrderStatus[] = ['DRAFT', 'PENDING'];
const LIFECYCLE_SUBMITTED: readonly SalesOrderStatus[] = ['SUBMITTED', 'APPROVED'];

/** Every operation `salesOrderLifecycleOperation` can name — the ops a template may not perform. */
const LIFECYCLE_OPS: readonly SalesOrderOp[] = [
  'submit',
  'approve',
  'start picking',
  'mark as shipped',
  'close',
  'revert',
  'cancel',
  'uncancel',
];

const CHANGE_REQUEST_STATUSES: readonly SalesOrderStatus[] = ['PENDING', 'APPROVED'];

const unavailable = (facts: SalesOrderFacts): Decision =>
  deny('SALES_ORDER_ACTION_UNAVAILABLE', `Not available while the sales order is ${getStatusLabel(facts.status)}.`);

const decideOperation = (facts: SalesOrderFacts, op: SalesOrderOp): Decision => {
  if (op === 'set status directly') {
    return deny('SALES_ORDER_STATUS_WRITE_BYPASSES_MACHINE', 'Use the status buttons to change status.');
  }

  if (facts.isTemplate && LIFECYCLE_OPS.includes(op)) return unavailable(facts);

  let isAvailable: boolean;
  switch (op) {
    case 'submit':
      isAvailable = facts.status === 'DRAFT';
      break;
    case 'approve':
      isAvailable = facts.status === 'PENDING';
      break;
    case 'start picking':
      isAvailable = LIFECYCLE_SUBMITTED.includes(facts.status);
      break;
    case 'mark as shipped':
      isAvailable = facts.status === 'PICKING' || LIFECYCLE_SUBMITTED.includes(facts.status);
      break;
    case 'close':
      isAvailable = facts.status === 'SHIPPED';
      break;
    case 'revert':
      isAvailable = facts.isCustomerCreated
        ? facts.status !== 'PENDING'
        : facts.status !== 'DRAFT' && facts.status !== 'CANCELLED';
      break;
    case 'cancel':
      isAvailable = LIFECYCLE_DRAFT.includes(facts.status) || LIFECYCLE_SUBMITTED.includes(facts.status);
      break;
    case 'uncancel':
      isAvailable = facts.status === 'CANCELLED';
      break;
    case 'duplicate':
      isAvailable = true;
      break;
    case 'add freight':
      isAvailable = facts.status === 'DRAFT';
      break;
    case 'generate document':
    case 'save as template':
      isAvailable = !facts.isTemplate;
      break;
    case 'set default template':
      isAvailable = facts.isTemplate;
      break;
    case 'handle change requests':
      // EDI 860 change requests are decided only while the order still sits at its EDI draft
      // (PENDING) or approved (APPROVED) status — the server's window, sentence verbatim.
      return CHANGE_REQUEST_STATUSES.includes(facts.status)
        ? allow()
        : deny(
            'SALES_ORDER_CHANGE_REQUESTS_LOCKED',
            'Change requests can be handled only while the order is Pending or Approved.'
          );
  }

  return isAvailable ? allow() : unavailable(facts);
};

const fieldRule = (facts: SalesOrderFacts, action: Action<SalesOrderField, SalesOrderOp>): Decision =>
  action.type === 'edit' && action.field !== undefined ? decideField(facts, action.field) : allow();

const fieldRules = {
  label: fieldRule,
  customer: fieldRule,
  customerContact: fieldRule,
  customerPO: fieldRule,
  orderDate: fieldRule,
  shipTo: fieldRule,
  billTo: fieldRule,
  salesTerms: fieldRule,
  salesPerson: fieldRule,
  shipFrom: fieldRule,
  currencyCode: fieldRule,
  exchangeRate: fieldRule,
  subsidiary: fieldRule,
  pricingSheet: fieldRule,
  salesOrderCustomField: fieldRule,
  shippingTerms: fieldRule,
  freightCompany: fieldRule,
  shipDate: fieldRule,
  deliveryDate: fieldRule,
  temperatureMin: fieldRule,
  temperatureMax: fieldRule,
  shipmentCustomField: fieldRule,
  flagged: fieldRule,
} satisfies Record<SalesOrderField, typeof fieldRule>;

export const salesOrderPolicy: Policy<SalesOrderFacts, SalesOrderField, SalesOrderOp> = definePolicy<
  SalesOrderFacts,
  SalesOrderField,
  SalesOrderOp
>({
  kind: 'sales order',
  fields: SALES_ORDER_FIELDS,
  ops: SALES_ORDER_OPS,
  requires: {},
  entity: (facts, action) => {
    if (action.type === 'view' || action.type === 'add' || action.type === 'edit') return allow();

    if (action.type === 'delete') {
      return facts.status === 'DRAFT'
        ? allow()
        : deny('SALES_ORDER_DELETE_LOCKED', 'Only Draft sales orders can be deleted.');
    }

    return decideOperation(facts, action.op);
  },
  field: fieldRules,
});
